import httpStatus from 'http-status-codes';
import sessionStorageService, { storageKeys } from '../../utils/sessionStorageService';
import {
  fetchPeriods as fetchPeriodsApi,
  fetchUserProfile as fetchUserProfileApi,
  fetchAppData as fetchAppDataApi,
  fetchProvisions as fetchProvisionsApi,
  fetchUserClientList as fetchUserClientListApi,
  fetchReportingCurrencyCodes as fetchReportingCurrencyCodesApi,
  fetchProvisionData as fetchProvisionDataApi,
  fetchAssignedConnections as fetchAssignedConnectionsApi,
  fetchConnections as fetchConnectionsApi,
  fetchConnectionsByType as fetchConnectionsByTypeApi,
  uploadBalanceFile as uploadBalanceFileApi,
  validateBalanceFile as validateBalanceFileApi,
  fetchAccounts as fetchAccountsApi,
  fetchBookTypes as fetchBookTypesApi,
  fetchCompanyCodeDetails as fetchCompanyCodeDetailsApi,
  fetchTaxYears as fetchTaxYearsApi,
  fetchCountryTaxTypeDescriptions as fetchCountryTaxTypeDescriptionsApi,
  fetchCategories as fetchCategoriesApi,
  fetchYearStartPeriod as fetchYearStartPeriodApi,
  saveArchiveReport as saveArchiveReportApi,
  fetchCalculationId as fetchCalculationIdApi,
} from '../api';
import {
  setProcessingBatchProvisionStatus as setProcessingBatchProvisionStatusApi,
  uploadBatchProvisions as uploadBatchProvisionsApi,
  processBatchProvisionsSync as processBatchProvisionsSyncApi,
} from '../../provisionPage/batchProvisionsPage/api';

import {
  FETCH_INITIAL_DATA,
  FETCH_INITIAL_DATA_SUCCESS,
  FETCHING_ERROR,
  SET_COMPANY_CODES_FILTER,
  UNSET_COMPANY_CODES_FILTER,
  FETCH_PROVISION_DATA,
  FETCH_PROVISION_DATA_SUCCESS,
  SELECT_COMPANY_CODE,
  SELECT_PERIOD,
  FETCH_TAX_YEARS,
  FETCH_TAX_YEARS_SUCCESS,
  SELECT_TAX_YEAR,
  SELECT_BOOK_TYPE,
  FETCH_COMPANY_CODE_DETAILS,
  FETCH_COMPANY_CODE_DETAILS_SUCCESS,
  FETCH_COMPANY_CODE_DETAILS_FAILURE,
  SELECT_CONNECTION,
  SELECT_CONNECTION_BY_TYPE,
  FETCH_CONNECTIONS,
  FETCH_CONNECTIONS_SUCCESS,
  FETCH_CONNECTIONS_BY_TYPE,
  FETCH_CONNECTIONS_BY_TYPE_SUCCESS,
  FETCH_CONNECTIONS_BY_TYPE_FAILURE,
  FETCH_PROVISIONS,
  FETCH_PROVISIONS_SUCCESS,
  FETCH_PROVISIONS_FAILURE,
  SHOW_ALERT,
  HIDE_ALERT,
  FETCH_PROVISION_DATA_FAILURE,
  SELECT_REPORTING_CURRENCY_CODE,
  FETCH_ACCOUNTS_STARTED,
  FETCH_ACCOUNTS_SUCCESS,
  FETCH_ACCOUNTS_FAILURE,
  FETCH_BOOK_TYPES_STARTED,
  FETCH_BOOK_TYPES_SUCCESS,
  FETCH_BOOK_TYPES_FAILURE,
  FETCH_USER_CLIENTS_LIST_SUCCESS,
  FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS,
  FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS_SUCCESS,
  FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS_FAILURE,
  RESET_STATE,
  FETCH_CATEGORIES,
  FETCH_PERIODS,
  FETCH_PERIODS_SUCCESS,
  FETCH_CALCULATION_ID,
  FETCH_CALCULATION_ID_SUCCESS,
  FETCH_CALCULATION_ID_FAILURE,
  SELECT_CALCULATION,
} from './types';

import {
  ADD_JOB,
  ADD_JOB_SUCCESS,
  ADD_JOB_FAILURE,
} from '../../shared/jim/store/types';

import chunk from 'lodash/chunk';
import { errorNotification, infoNotification, successNotification } from '../../shared/store/notificationActions';
import { readTrialBalanceXlsxFileAsJson } from '../../provisionPage/readXlsxFileAsJson';
import alertTypes from '../alertTypes';
import Config from '../../config';
import { getFileExtension } from '@common/utils/fileNameHelpers';
import { companyCodesOptions } from './selectors';
import { changeSlGroupType } from '../../balanceSheetManagement/aggregationMenu/store/sharedActions';
import { COMPANY_CODE_TYPE_VALUE } from '../../balanceSheetManagement/aggregationMenu/constants';
import { YES_FLAG, NO_FLAG } from '../constants';
import { fetchActionFactory } from './fetchFactory';
import modalNames from '../../administration/beginningBalancesPage/modalNames';
import getModalActions from '../modals/getModalActions';
import { hideImportBatchBeginningBalancesModal } from '../../administration/beginningBalancesPage/store/actions';
import { AddJobOnMaster } from '../../shared/jim/store/actions';
import { renderDate } from '../../shared/dateHelpers';

const fetchInitialDataSuccess = ({
  app,
  companyCodes,
  currentPeriod,
  periods,
  period,
  user,
  clients,
  maintenance,
  reportingCurrencyCodes,
  reportingCurrencyCode,
  taxYears,
  yearStartPeriod,
}) => ({
  type: FETCH_INITIAL_DATA_SUCCESS,
  app,
  companyCodes,
  currentPeriod,
  periods,
  period,
  user,
  clients,
  maintenance,
  reportingCurrencyCodes,
  reportingCurrencyCode,
  taxYears,
  yearStartPeriod,
});

export const fetchingError = errorMessage => ({
  type: FETCHING_ERROR,
  errorMessage,
});

const fetchingErrorHandler = (dispatch, error) => {
  if (error.isAuthorizationError) {
    return;
  }
  if (error.isFetchingError && error.hasAdditionalInfo) {
    dispatch(fetchingError(error.message));
    return;
  }
  if (error.isFetchingError) {
    dispatch(fetchingError());
    dispatch(errorNotification(error.message));
    return;
  }
  throw error;
};

export const fetchInitialData = () => async (dispatch, getState) => {
  dispatch({
    type: FETCH_INITIAL_DATA,
  });

  try {
    const { currentPeriod, periods } = await fetchPeriodsApi();
    const { period, reportingCurrencyCode } = getState().shared;

    const periodToSelect = period || currentPeriod;

    const [
      user,
      app,
      companyCodes,
      clientsListWithMaintenanceStatus,
      reportingCurrencyCodes,
      taxYears,
      yearStartPeriod,
    ] = await Promise.all([
      fetchUserProfileApi(),
      fetchAppDataApi(),
      fetchProvisionsApi(periodToSelect),
      fetchUserClientListApi(),
      fetchReportingCurrencyCodesApi(),
      fetchTaxYearsApi(),
      fetchYearStartPeriodApi(),
    ]);

    user.isActive = user.isActive === YES_FLAG;
    const initialReportingCurrencyCode = reportingCurrencyCode
      && reportingCurrencyCodes.some(({ value }) => value === reportingCurrencyCode)
      ? reportingCurrencyCode
      : reportingCurrencyCodes[0] && reportingCurrencyCodes[0].value;

    const currentClientId = await sessionStorageService.getItem(storageKeys.CLIENT_KEY);
    if (currentClientId === null) {
      await sessionStorageService.setItem(storageKeys.CLIENT_KEY, user.lastClientId);
    }

    dispatch(fetchInitialDataSuccess({
      app,
      companyCodes,
      currentPeriod,
      periods,
      period: periodToSelect,
      user,
      clients: clientsListWithMaintenanceStatus.clients,
      maintenance: clientsListWithMaintenanceStatus.maintenance,
      reportingCurrencyCodes,
      reportingCurrencyCode: initialReportingCurrencyCode,
      taxYears,
      yearStartPeriod,
    }));
  } catch (error) {
    fetchingErrorHandler(dispatch, error);
  }
};

export const fetchProvisions = period => async dispatch => {
  const fetchProvisionsSuccess = companyCodes => ({
    type: FETCH_PROVISIONS_SUCCESS,
    companyCodes,
  });

  dispatch({ type: FETCH_PROVISIONS });

  try {
    const companyCodes = await fetchProvisionsApi(period);

    dispatch(fetchProvisionsSuccess(companyCodes));
  } catch (error) {
    dispatch({ type: FETCH_PROVISIONS_FAILURE });
    fetchingErrorHandler(dispatch, error);
  }
};

export const setCompanyCodesFilter = filter => ({
  type: SET_COMPANY_CODES_FILTER,
  filter,
});

export const unsetCompanyCodesFilter = () => ({
  type: UNSET_COMPANY_CODES_FILTER,
});

export const fetchProvisionData = (companyCode, fetchingInitialProvisionData = false) => async (dispatch, getState) => {
  const fetchProvisionDataSuccess = provisionData => ({
    type: FETCH_PROVISION_DATA_SUCCESS,
    provisionData,
  });

  const period = getState().shared.period;

  if (!companyCode || !period) {
    return;
  }

  dispatch({
    type: FETCH_PROVISION_DATA,
    fetchingInitialProvisionData,
  });

  try {
    const responseData = await fetchProvisionDataApi({
      companyCode,
      period: period.name,
    });

    return dispatch(fetchProvisionDataSuccess(responseData));
  } catch (error) {
    dispatch({
      type: FETCH_PROVISION_DATA_FAILURE,
    });
    fetchingErrorHandler(dispatch, error);
  }
};

export const selectCompanyCode = companyCode => dispatch => {
  dispatch({
    type: SELECT_COMPANY_CODE,
    companyCode,
  });

  dispatch(changeSlGroupType(COMPANY_CODE_TYPE_VALUE));
};

export const selectPeriod = (period, shouldFetchProvisions = true) => dispatch => {
  if (!period) {
    return;
  }

  dispatch({
    type: SELECT_PERIOD,
    period,
  });

  if (shouldFetchProvisions) {
    dispatch(fetchProvisions(period));
  }
};

export const fetchTaxYears = () => async dispatch => {
  const fetchTaxYearsSuccess = taxYears => ({
    type: FETCH_TAX_YEARS_SUCCESS,
    taxYears,
  });

  dispatch({
    type: FETCH_TAX_YEARS,
  });

  try {
    const responseData = await fetchTaxYearsApi();

    return dispatch(fetchTaxYearsSuccess(responseData.taxYears));
  } catch (error) {
    fetchingErrorHandler(dispatch, error);
  }
};

export const fetchPeriods = () => async dispatch => {
  dispatch({
    type: FETCH_PERIODS,
  });

  try {
    const { currentPeriod, periods } = await fetchPeriodsApi();

    return dispatch({
      type: FETCH_PERIODS_SUCCESS,
      periods,
      currentPeriod,
    });
  } catch (error) {
    fetchingErrorHandler(dispatch, error);
  }
};

export const selectTaxYear = taxYear => ({
  type: SELECT_TAX_YEAR,
  taxYear,
});

export const selectBookType = bookType => ({
  type: SELECT_BOOK_TYPE,
  bookType,
});

export const fetchCompanyCodeDetails = (companyCode, period) => async dispatch => {
  const fetchCompanyCodeDetailsSuccess = companyCodeDetails => ({
    type: FETCH_COMPANY_CODE_DETAILS_SUCCESS,
    companyCodeDetails,
  });

  const fetchCompanyCodeDetailsFailure = error => ({
    type: FETCH_COMPANY_CODE_DETAILS_FAILURE,
    error,
  });

  if (!companyCode || !period) {
    return;
  }

  try {
    dispatch({
      type: FETCH_COMPANY_CODE_DETAILS,
    });

    const responseData = await fetchCompanyCodeDetailsApi({ companyCode, period: period.name });

    return dispatch(fetchCompanyCodeDetailsSuccess(responseData));
  } catch (error) {
    if (error.isFetchingError && error.hasAdditionalInfo) {
      return dispatch(fetchCompanyCodeDetailsFailure(error.message));
    }
    fetchingErrorHandler(dispatch, error);
  }
};

export const fetchConnectionsByType = (
  period,
  connType
) => async (dispatch, getState) => {
  const { connection } = getState().shared;

  const fetchConnectionsByTypeSuccess = connectionsByType => {
    let connectionToSelect = null;

    if (connection && connection.connName !== 'ALL') {
      connectionToSelect = connection;
    } else {
      connectionToSelect = connectionsByType[0];
    }

    return {
      type: FETCH_CONNECTIONS_BY_TYPE_SUCCESS,
      connectionsByType,
      connectionByType: connectionToSelect,
    };
  };

  try {
    if (!period) {
      return;
    }

    dispatch({
      type: FETCH_CONNECTIONS_BY_TYPE,
    });

    const responseData = await fetchConnectionsByTypeApi({ period: period.name, connType });

    return dispatch(fetchConnectionsByTypeSuccess(responseData));
  } catch (error) {
    dispatch({
      type: FETCH_CONNECTIONS_BY_TYPE_FAILURE,
    });
    fetchingErrorHandler(dispatch, error);
  }
};

export const selectConnection = (value, preselect = false) => (dispatch, getState) => {
  if (preselect) {
    return dispatch({
      type: SELECT_CONNECTION,
      connection: { connName: value },
    });
  }
  const connections = getState().shared.connections;
  const connection = connections.find(connection => connection.connName === value);
  const action = {
    type: SELECT_CONNECTION,
    connection,
  };

  return dispatch(action);
};

export const selectConnectionByType = (value, preselect = false) => (dispatch, getState) => {
  if (preselect) {
    return dispatch({
      type: SELECT_CONNECTION_BY_TYPE,
      connectionByType: { connName: value },
    });
  }
  const connections = getState().shared.connectionsByType;
  const connectionByType = connections.find(connection => connection.connName === value);
  const action = {
    type: SELECT_CONNECTION_BY_TYPE,
    connectionByType,
  };

  return dispatch(action);
};

export const fetchConnections = (
  period,
  fetchingOptions
) =>
  async (dispatch, getState) => {
    const options = {
      limitToAssigned: false,
      includeAllOption: true,
      setConnection: true,
      setAllConnectionsByDefault: false,
      ...fetchingOptions,
    };

    const { connection } = getState().shared;

    const fetchConnectionsSuccess = connections => {
      let connectionToSelect = null;

      if (options.setAllConnectionsByDefault) {
        connectionToSelect = { connName: 'ALL' };
      } else if (connection && connection.connName !== 'ALL') {
        connectionToSelect = connection;
      } else if (options.setConnection) {
        connectionToSelect = connections[0];
      }

      return {
        type: FETCH_CONNECTIONS_SUCCESS,
        connections,
        connection: connectionToSelect,
      };
    };

    try {
      if (!period) {
        return;
      }

      dispatch({
        type: FETCH_CONNECTIONS,
      });

      const params = {
        period: period.name,
      };
      let responseData;

      responseData = options.limitToAssigned ? await fetchAssignedConnectionsApi(params)
        : await fetchConnectionsApi(params);

      if (responseData.length && options.includeAllOption) {
        responseData = [
          { connName: 'ALL' },
          ...responseData,
        ];
      }

      return dispatch(fetchConnectionsSuccess(responseData));
    } catch (error) {
      if (error.isAuthorizationError) {
        return;
      }
      if (error.isFetchingError) {
        dispatch(fetchingError());
        dispatch(errorNotification(error.message));
        return;
      }
      throw error;
    }
  };

export const fetchCalculationId = (connName, periodName) => async dispatch => {
  dispatch({
    type: FETCH_CALCULATION_ID,
  });

  try {
    const calculationIdList = await fetchCalculationIdApi( connName, periodName );

    return dispatch({
      type: FETCH_CALCULATION_ID_SUCCESS,
      calculationIdList: calculationIdList.map(calculationId => ({
        calcJobId: calculationId.calcJobId,
        dateCreated: renderDate(calculationId.dateCreated),
      })),
    });
  } catch (error) {
    dispatch({
      type: FETCH_CALCULATION_ID_FAILURE,
    });
    fetchingErrorHandler(dispatch, error);
  }
};

export const selectCalculation = (calculationSelected, preselect = false) => (dispatch, getState) => {
  if (preselect) {
    return dispatch({
      type: SELECT_CALCULATION,
      calculationIdSelected: { label: `${calculationSelected.calcJobId} - ${calculationSelected.dateCreated}`, value: calculationSelected.calcJobId },
    });
  }
  const calculations = getState().shared.calculationIdList;
  const calculationId = calculations.find(calculationId => calculationId.calcJobId === calculationSelected.value);
  const action = {
    type: SELECT_CALCULATION,
    calculationIdSelected: {
      label: `${calculationId.calcJobId} - ${calculationId.dateCreated}`,
      value: calculationId.calcJobId,
    },
  };

  return dispatch(action);
};

export const addJobOnMasterAction = ({ enqueueJob }) => async dispatch => {
  try {
    await dispatch({
      type: ADD_JOB,
    });
    dispatch(infoNotification('Requesting Generate Journal File Processing...'));

    await dispatch(
      AddJobOnMaster({
        jobType: Config.jimJobs.generateJournalFile.type,
        enqueueJob: {
          ...enqueueJob,
          jobDisplayName: Config.jimJobs.generateJournalFile.displayName,
        },
      })
    );

    dispatch({
      type: ADD_JOB_SUCCESS,
    });
  } catch (error) {
    dispatch(errorNotification('Requesting Generate Journal File Failed...'));
    dispatch(fetchingError());
    dispatch({
      type: ADD_JOB_FAILURE,
    });
  }
};


export const showAlert = (content, alertType) => ({
  type: SHOW_ALERT,
  content,
  alertType,
});

export const hideAlert = () => ({
  type: HIDE_ALERT,
});

const checkImportFileFormat = (file, messageLabel) => {
  const MAX_FILE_SIZE_100_MB = 100 * 1024 * 1024;

  const acceptedMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];

  const acceptedExtensions = [
    'xls',
    'xlsx',
  ];

  if (!acceptedMimeTypes.includes(file.type) && !acceptedExtensions.includes(getFileExtension(file.name))) {
    const error = {
      message: `File selected is not an Excel file (.xls or .xlsx). Please select an Excel file to import ${messageLabel}.`,
    };

    throw error;
  }

  if (file.size > MAX_FILE_SIZE_100_MB) {
    const error = {
      message: `File size limit exceeded. Limit is 100MB. (${file.size / 1024 / 1024}MB)`,
    };

    throw error;
  }
};

const {
  showAction: showImportBeginningBalancesValidationModal,
  hideAction: hideImportBeginningBalancesValidationModal,
} = getModalActions(modalNames.ImportBatchBeginningBalancesValidation);
export { hideImportBeginningBalancesValidationModal };

export const uploadBalanceFileAction = ({
  action,
  successAction,
  failureAction,

  validationData,

  isBeginningBalance,
  companyCode,
  messageLabel,
  file,
}) => async dispatch => {
  try {
    await dispatch(action());
    dispatch(infoNotification(`${messageLabel} File Upload Processing...`));

    checkImportFileFormat(file, messageLabel);

    const mappedFile = await readTrialBalanceXlsxFileAsJson(file, validationData, isBeginningBalance);

    const { validationErrors } = await validateBalanceFileApi({
      file: mappedFile,
      isBeginningBalance,
    });

    if (validationErrors) {
      dispatch(hideImportBatchBeginningBalancesModal());
      dispatch(showImportBeginningBalancesValidationModal({ validationErrors }));
      dispatch(failureAction());
      return;
    }

    const { period } = validationData;

    await uploadBalanceFileApi({
      companyCode,
      period: period.name,
      file: mappedFile,
      isBeginningBalance: isBeginningBalance ? YES_FLAG : NO_FLAG,
    });

    await dispatch(successAction());
    dispatch(successNotification(`${messageLabel} File Upload Successful`));
  } catch (error) {
    dispatch(fetchingError());
    dispatch(failureAction());
    if (error.status === httpStatus.BAD_REQUEST) {
      return dispatch(errorNotification('The import resulted in 0 records saved. Please examine the Excel import file and try again'));
    }

    dispatch(showAlert(error.message, alertTypes.DANGER));
  }
};

export const uploadBatchBalancesFileAction = ({
  file,
  connectionName,
  adjustmentPeriodName,
  isBeginningBalance,
  calculateProvisions,
  loadTrialBalances,
  skipFileUpload,
  messageLabel,

  validationData,

  action,
  successAction,
  failureAction,
}) => async dispatch => {
  await dispatch(action());

  try {
    let batchId = null;
    let logHeaderId = null;

    if (!skipFileUpload) {
      checkImportFileFormat(file, messageLabel);

      const mappedFile = await readTrialBalanceXlsxFileAsJson(file, validationData, isBeginningBalance);
      dispatch(infoNotification(`${messageLabel} File Processing...`));

      const { validationErrors } = await validateBalanceFileApi({
        file: mappedFile,
        isBeginningBalance,
      });

      if (validationErrors) {
        dispatch(hideImportBatchBeginningBalancesModal());
        dispatch(showImportBeginningBalancesValidationModal({ validationErrors }));
        dispatch(failureAction());
        return;
      }


      const chunkedFile = chunk(mappedFile, Config.batchProcessing.maxRowsPerRequest);

      const result = await setProcessingBatchProvisionStatusApi({
        connectionName,
        periodName: adjustmentPeriodName,
      });

      logHeaderId = result.logHeaderId;

      for (const singleArray of chunkedFile) {
        try {
          const parsedResponse = await uploadBatchProvisionsApi({
            batchId,
            connectionName,
            collection: singleArray,
            periodName: adjustmentPeriodName,
            logHeaderId,
            isBeginningBalance,
          });

          batchId = parsedResponse.batchId;
        } catch (error) {
          dispatch(failureAction());

          if (error.isFetchingError) {
            return dispatch(errorNotification(error.message));
          }

          throw error;
        }
      }
    }

    try {
      await processBatchProvisionsSyncApi({
        batchId,
        connectionName,
        periodName: adjustmentPeriodName,
        calculateProvisions,
        loadTrialBalances,
        logHeaderId,
        isBeginningBalance: isBeginningBalance ? YES_FLAG : NO_FLAG,
      });
    } catch (error) {
      dispatch(failureAction());

      if (error.isFetchingError) {
        return dispatch(errorNotification(error.message));
      }

      throw error;
    }

    dispatch(successAction());
  } catch (error) {
    dispatch(failureAction());
    return dispatch(showAlert(error.message, alertTypes.DANGER));
  }
};

export const selectReportingCurrencyCode = reportingCurrencyCode => dispatch => {
  dispatch({ type: SELECT_REPORTING_CURRENCY_CODE, reportingCurrencyCode });
};

export const selectCompanyCodeIfExists = companyCode => (dispatch, getState) => {
  const companyCodes = companyCodesOptions(getState());

  if (companyCodes.find(({ value }) => value === companyCode)) {
    dispatch({
      type: SELECT_COMPANY_CODE,
      companyCode,
    });
  }
};

export const fetchAccounts = () => async dispatch => {
  const fetchAccountsStarted = () => ({
    type: FETCH_ACCOUNTS_STARTED,
  });

  const fetchAccountsSuccess = accounts => ({
    type: FETCH_ACCOUNTS_SUCCESS,
    accounts,
  });

  const fetchAccountsFailure = () => ({
    type: FETCH_ACCOUNTS_FAILURE,
  });

  dispatch(fetchAccountsStarted());

  try {
    const accounts = await fetchAccountsApi();
    dispatch(fetchAccountsSuccess(accounts));
  } catch (error) {
    dispatch(fetchAccountsFailure());
    if (error.isAuthorizationError) {
      return;
    }
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchUserClientList = () => async (dispatch, getState) => {
  try {
    const clients = await fetchUserClientListApi();
    const lastClientId = getState().user.user?.lastClientId;
    dispatch({
      type: FETCH_USER_CLIENTS_LIST_SUCCESS,
      clients,
      lastClientId,
    });
  } catch (error) {
    if (error.isAuthorizationError) {
      return;
    }
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchBookTypes = () => async (dispatch, getState) => {
  const period = getState().shared.period;
  if (!period) {
    dispatch(errorNotification('Period not selected'));
  }

  dispatch({
    type: FETCH_BOOK_TYPES_STARTED,
  });

  try {
    const bookTypes = await fetchBookTypesApi({ period: period.name });
    return dispatch({
      type: FETCH_BOOK_TYPES_SUCCESS,
      bookTypes,
    });
  } catch (error) {
    dispatch({
      type: FETCH_BOOK_TYPES_FAILURE,
    });
    if (error.isAuthorizationError) {
      return;
    }
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchCountryTaxTypeDescriptions = period => async dispatch => {
  dispatch({
    type: FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS,
  });

  try {
    const response = await fetchCountryTaxTypeDescriptionsApi({ periodName: period.name });
    const countryTaxTypeDescriptions = response.reduce(( result, countryTaxTypeDescription ) => {
      result[countryTaxTypeDescription.countryTaxType] = countryTaxTypeDescription.description;
      return result;
    }, {});

    return dispatch({
      type: FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS_SUCCESS,
      countryTaxTypeDescriptions,
    });
  } catch (error) {
    dispatch({
      type: FETCH_COUNTRY_TAX_TYPE_DESCRIPTIONS_FAILURE,
    });
    if (error.isAuthorizationError) {
      return;
    }
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};


export const saveArchiveReport = (batchId, payload) => async dispatch => {
  try {
    await saveArchiveReportApi(batchId, payload);
    dispatch(successNotification('Defaults saved successfully'));
  } catch (error) {
    if (error.isAuthorizationError) {
      return;
    }
    throw error;
  }
};

export const fetchCategories = fetchActionFactory(
  FETCH_CATEGORIES,
  fetchCategoriesApi,
  { field: 'categories' }
);

export const resetState = () => ({ type: RESET_STATE });
